import { useState, useEffect } from 'react';
import { useGameStore } from '../store/gameStore';
import './Lobby.css';

interface GameInfo {
  gameId: string;
  mode: 'standard' | 'extended';
  playerCount: number;
  maxPlayers: number;
  phase: string;
  createdAt: string;
}

const ENV = (import.meta as unknown as { env: { DEV: boolean; VITE_API_BASE?: string } }).env;
const API_BASE = ENV.DEV
  ? 'http://localhost:8787'
  : (ENV.VITE_API_BASE || 'https://catan-web.chao200222.workers.dev');

export default function Lobby() {
  const { 
    playerName, 
    setPlayerName, 
    createGame, 
    joinGame, 
    isLoading, 
    error,
    clearError 
  } = useGameStore();
  
  const [joinCode, setJoinCode] = useState('');
  const [mode, setMode] = useState<'standard' | 'extended'>('standard');
  const [gameList, setGameList] = useState<GameInfo[]>([]);
  const [isLoadingGames, setIsLoadingGames] = useState(false);

  // ゲーム一覧を取得
  const fetchGameList = async () => {
    setIsLoadingGames(true);
    try {
      const response = await fetch(`${API_BASE}/api/games`);
      if (response.ok) {
        const data = await response.json() as { games: GameInfo[] };
        setGameList(data.games || []);
      }
    } catch (e) {
      console.error('Failed to fetch games:', e);
    } finally {
      setIsLoadingGames(false);
    }
  };

  // 初回読み込みと定期更新
  useEffect(() => {
    fetchGameList();
    const interval = setInterval(fetchGameList, 5000); // 5秒ごとに更新
    return () => clearInterval(interval);
  }, []);

  const handleCreate = () => {
    if (!playerName.trim()) {
      alert('名前を入力してください');
      return;
    }
    createGame(mode);
  };

  const handleJoin = () => {
    if (!playerName.trim()) {
      alert('名前を入力してください');
      return;
    }
    if (!joinCode.trim()) {
      alert('ゲームIDを入力してください');
      return;
    }
    joinGame(joinCode.trim());
  };

  const handleQuickJoin = (gameId: string) => {
    if (!playerName.trim()) {
      alert('名前を入力してください');
      return;
    }
    joinGame(gameId);
  };

  return (
    <div className="lobby">
      <div className="lobby-bg">
        <div className="hex-pattern"></div>
      </div>
      
      <div className="lobby-content animate-slideUp">
        <div className="lobby-logo">
          <div className="logo-hex">
            <span>C</span>
          </div>
          <h1>カタン オンライン</h1>
          <p className="lobby-subtitle">友達と一緒にプレイ！</p>
        </div>

        {error && (
          <div className="lobby-error animate-shake" onClick={clearError}>
            {error}
          </div>
        )}

        <div className="lobby-card">
          <div className="input-group">
            <label>あなたの名前</label>
            <input
              type="text"
              className="input"
              placeholder="例: たろう"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              maxLength={20}
            />
          </div>

          <div className="mode-selector">
            <button
              className={`mode-btn ${mode === 'standard' ? 'active' : ''}`}
              onClick={() => setMode('standard')}
            >
              <span className="mode-icon">👥</span>
              <span className="mode-text">
                <strong>スタンダード</strong>
                <small>2-4人</small>
              </span>
            </button>
            <button
              className={`mode-btn ${mode === 'extended' ? 'active' : ''}`}
              onClick={() => setMode('extended')}
            >
              <span className="mode-icon">👨‍👩‍👧‍👦</span>
              <span className="mode-text">
                <strong>拡張版</strong>
                <small>4-6人</small>
              </span>
            </button>
          </div>

          <button 
            className="btn btn-primary lobby-btn"
            onClick={handleCreate}
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="loading-spinner-sm"></span>
            ) : (
              <>
                <span>🎮</span>
                新しいゲームを作成
              </>
            )}
          </button>

          <div className="lobby-divider">
            <span>または</span>
          </div>

          {/* 待機中のゲーム一覧 */}
          {gameList.length > 0 && (
            <div className="game-list-section">
              <label>🎯 待機中のゲーム</label>
              <div className="game-list">
                {gameList.map(game => (
                  <div key={game.gameId} className="game-list-item">
                    <div className="game-info">
                      <span className="game-mode">{game.mode === 'standard' ? '👥' : '👨‍👩‍👧‍👦'}</span>
                      <span className="game-players">{game.playerCount}/{game.maxPlayers}</span>
                      <span className="game-id-short">{game.gameId.slice(0, 8)}...</span>
                    </div>
                    <button 
                      className="btn btn-sm btn-join"
                      onClick={() => handleQuickJoin(game.gameId)}
                      disabled={isLoading}
                    >
                      参加
                    </button>
                  </div>
                ))}
              </div>
              <button 
                className="btn-refresh"
                onClick={fetchGameList}
                disabled={isLoadingGames}
              >
                {isLoadingGames ? '更新中...' : '🔄 更新'}
              </button>
            </div>
          )}

          <div className="lobby-divider">
            <span>IDで参加</span>
          </div>

          <div className="input-group">
            <label>ゲームIDで参加</label>
            <input
              type="text"
              className="input"
              placeholder="ゲームIDを入力"
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value)}
            />
            <small className="input-hint">右上の「コピー」でフルのゲームIDを使用してください</small>
          </div>

          <button 
            className="btn btn-outline lobby-btn"
            onClick={handleJoin}
            disabled={isLoading || !joinCode.trim()}
          >
            <span>🚀</span>
            ゲームに参加
          </button>
        </div>

        <div className="lobby-footer">
          <p>📱 スマホ・タブレット対応</p>
        </div>
      </div>
    </div>
  );
}
